"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const navLinks = [
  { href: "/", label: "Accueil" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    let rafId: number | null = null;

    const updateLogoVisibility = () => {
      const indicator = document.getElementById("hero-scroll-indicator");
      const navbar = document.getElementById("site-navbar");

      if (!indicator || !navbar) {
        setLogoProgress(1);
        return;
      }

      const indicatorRect = indicator.getBoundingClientRect();
      const navbarRect = navbar.getBoundingClientRect();
      const transitionStart = navbarRect.bottom + 80;
      const transitionEnd = navbarRect.bottom - 8;
      const rawProgress =
        (transitionStart - indicatorRect.top) / (transitionStart - transitionEnd);
      const nextProgress = Math.max(0, Math.min(1, rawProgress));
      setLogoProgress(nextProgress);
    };

    const requestUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateLogoVisibility();
      });
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    requestUpdate();

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <nav id="site-navbar" className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 py-4 relative flex items-center justify-center">

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <Link
            href={navLinks[0].href}
            className="text-sm font-sans text-white transition-colors duration-300"
          >
            {navLinks[0].label}
          </Link>

          <button
            type="button"
            onClick={scrollToTop}
            className={`relative w-8 h-8 transition-opacity duration-300 ${
              logoProgress > 0 ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            style={{
              opacity: logoProgress,
              transform: `translateY(${(1 - logoProgress) * 14}px) scale(${0.72 + logoProgress * 0.28})`,
            }}
            aria-label="Retourner en haut de la page"
            aria-hidden={logoProgress === 0}
          >
            <Image
              src="/rares_corp.png"
              alt="Rares Corp"
              fill
              sizes="32px"
              className="object-contain brightness-0 invert"
            />
          </button>

          <Link
            href={navLinks[1].href}
            className="text-sm font-sans text-white transition-colors duration-300"
          >
            {navLinks[1].label}
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-white absolute right-6"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden glass border-t border-border animate-fade-in">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-sans text-white transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
